// Treatment Wizard configuration.
// Source: client "treatment suggestion flow.csv" + "Blue_Wolf_Decision_Tree.png" (draft; clinical
// rules and copy require clinic approval). Changing screening limits or routing = code review.

export const BMI_LIMITS = {
    tshape: 30, // inclusive
    lipo: 40, // inclusive
    skinExcision: 40, // inclusive
};

export const DEFAULT_CONSULTATION_URL = 'https://book.bluewolfbody.com/widget/booking/ly8hQdeqeYdXzOF87jcC';

export const DEFAULT_DISCLAIMER =
    'General information only; not medical advice or a determination of candidacy. Treatment suitability, risks, recovery, and expected results require an individual assessment by a qualified clinician.';

export const AREAS = [
    { id: 'belly', label: 'Belly/Abdomen Area' },
    { id: 'midsection', label: 'Entire 360 Midsection (belly, sides, back)' },
    { id: 'thighs', label: 'Thighs' },
    { id: 'arms', label: 'Arms' },
    { id: 'chin', label: 'Chin' },
    { id: 'chest', label: 'Male Chest Reduction' },
    { id: 'other', label: 'Other' },
];

export const QUESTIONS = {
    areas: {
        title: 'Which areas would you like to change?',
        hint: 'Select all that apply.',
    },
    priority: {
        title: 'Which of these areas is your top priority?',
        hint: 'Choose the one you would most like to change.',
    },
    measure: {
        title: 'Tell us your height and weight',
        hint: 'We use these to calculate your BMI. Nothing you enter is sent or stored.',
    },
    concern: {
        title: 'What bothers you most about that area?',
        hint: '',
        options: [
            { id: 'fat', label: 'Stubborn Fat' },
            { id: 'skin', label: 'Loose Skin' },
            { id: 'both', label: 'Both Fat & Skin' },
        ],
    },
    description: {
        title: 'In the area you want to change most, what can you feel or see?',
        hint: '',
        options: [
            { id: 'doughy', label: 'A soft, doughy bulge I can pinch/squeeze' },
            { id: 'loose', label: 'Mostly loose skin that I can pull away' },
            { id: 'both', label: 'Both a pinchable bulge and loose skin' },
            { id: 'firm', label: "My belly feels firm or rounded, and I can't grab much fat" },
            { id: 'unsure', label: "I can't tell" },
        ],
    },
    abdomen: {
        title: 'Is loose skin on your abdomen a concern for you?',
        hint: 'You selected the entire 360 midsection. Skin excision addresses the abdomen rather than the full 360 midsection.',
        options: [
            { id: 'yes', label: 'Yes, I have loose skin on my abdomen' },
            { id: 'no', label: 'No, not on my abdomen' },
        ],
    },
    downtime: {
        title: 'How much recovery time could you realistically make room for?',
        hint: '',
        options: [
            { id: 'none', label: "I can't take any time off, lunch hour treatments are all I can manage" },
            { id: 'short', label: 'I could manage about 1-3 days of downtime' },
            { id: 'flexible', label: 'Downtime is not a concern' },
        ],
    },
    change: {
        title: 'How much change are you hoping to see?',
        hint: '',
        options: [
            { id: 'subtle', label: 'A subtle improvement' },
            { id: 'noticeable', label: 'A noticeable change in my shape' },
            { id: 'major', label: 'A major transformation' },
        ],
    },
    budget: {
        title: 'What budget would you be comfortable considering?',
        hint: '',
        options: [
            { id: 'low', label: 'Around $350–$1,800' },
            { id: 'mid', label: 'Around $2,000–$5,000' },
            { id: 'high', label: 'Around $6,000–$9,000' },
            { id: 'top', label: 'Around $10,000–$15,000' },
            { id: 'undecided', label: 'I need to see treatment options and prices before setting a budget' },
        ],
    },
    weightChange: {
        title: 'Have you had a significant change in your weight?',
        hint: '',
        options: [
            { id: 'stable', label: 'Stayed within about 15 pounds' },
            { id: 'lost16', label: 'Lost 16–50 pounds' },
            { id: 'lost50', label: 'Lost more than 50 pounds' },
            { id: 'gained', label: 'Gained more than 15 pounds' },
        ],
    },
};

export const WEIGHT_CHANGE_LABELS = Object.fromEntries(QUESTIONS.weightChange.options.map((o) => [o.id, o.label]));

const BTN_SCHEDULE = 'Schedule a consultation';

// Main routing results (message copy from the CSV "Main Routing" tab).
export const RESULTS = {
    CONSULT_BMI: {
        headline: "Let's discuss your next steps",
        message:
            'Your calculated BMI is above our usual screening limits for T-Shape 2 and awake liposuction. A consultation is still available to discuss your goals and next steps.',
        button: BTN_SCHEDULE,
    },
    CONSULT_CLARIFY_UNSURE: {
        headline: "Let's help you narrow down your options",
        message:
            'We need a little more information to provide useful guidance. A consultation can help clarify your concerns and the options worth discussing.',
        button: BTN_SCHEDULE,
    },
    CONSULT_CLARIFY_CONFLICT: {
        headline: "Let's take a closer look",
        message:
            'Your answers describe different concerns. A consultation can help clarify whether fat, loose skin, or a combination is your main concern.',
        button: BTN_SCHEDULE,
    },
    CONSULT_ASSESS: {
        headline: 'An assessment is the best next step',
        message:
            'Based on this description, we need more information before suggesting a body contouring option. Please schedule a consultation to discuss your goals.',
        button: BTN_SCHEDULE,
    },
    CONSULT_EXPECTATIONS: {
        headline: "Let's find a realistic path toward your goals",
        message:
            'Your desired change may require more flexibility in budget, recovery time, or the amount of improvement you expect. A consultation can help you compare the possibilities.',
        button: BTN_SCHEDULE,
    },
    EDUCATION_FAT: {
        headline: 'Explore fat reduction options',
        message:
            'Your answers suggest that fat reduction is your main interest. Review the options below, then schedule a consultation for an individual assessment.',
        button: BTN_SCHEDULE,
    },
    EDUCATION_SKIN: {
        headline: 'Explore options for loose skin',
        message:
            'Your answers suggest that loose skin is your main interest. Review the options below, then schedule a consultation to discuss your goals and suitability.',
        button: BTN_SCHEDULE,
    },
    EDUCATION_COMBINED: {
        headline: 'Explore fat and skin treatment options',
        message:
            'Your answers suggest that both fat and loose skin are concerns. A consultation can help determine whether one treatment or a combined approach is worth considering.',
        button: BTN_SCHEDULE,
    },
    CONSULT_GENERAL: {
        headline: "Let's create a personalized plan",
        message:
            "Your answers don't point to one clear path. A consultation will help us understand your goals and discuss appropriate options.",
        button: BTN_SCHEDULE,
    },
};

export const MULTIPLE_OPTIONS = {
    headline: 'Multiple options may work for you',
    intro: 'Based on your answers, several options may be worth exploring.',
    allThree:
        'Based on your answers, T-Shape 2, awake liposuction, and awake skin excision may all be worth exploring. T-Shape 2 offers a non-invasive approach to body contouring; awake liposuction focuses on fat removal; and awake skin excision focuses on removing excess skin. A consultation will help us compare these options—or a combined approach—based on your goals, budget, and recovery preferences.',
    button: 'Compare my options',
};

// Treatment education cards (CSV content table). ACF "Treatment Education Cards" override these by ID.
export const DEFAULT_CARDS = {
    TSHAPE: {
        heading: 'T-Shape 2: Explore a non-invasive approach',
        body: 'Interested in a non-invasive approach? A consultation can help you understand T-Shape 2, the treatment plan, pricing, and whether it fits your goals.',
    },
    LIPO: {
        heading: 'Awake liposuction: Explore fat removal',
        body: 'Your answers identify pinchable fat as a concern. Awake liposuction is an option to discuss at a consultation, where we can assess the area and explain expected results, recovery, and pricing.',
    },
    SKIN_EXCISION: {
        heading: 'Awake skin excision: Explore skin removal',
        body: 'Your answers identify loose skin as a concern. A consultation can help determine whether awake skin excision is available for your selected area and appropriate for your goals. We’ll discuss the procedure, scarring, recovery, and pricing.',
    },
    COMBINED: {
        heading: 'Explore an approach to both fat and loose skin',
        body: 'You’ve described both pinchable fat and loose skin. We can discuss whether awake liposuction, skin excision, or a combined approach could fit your goals and selected area.',
    },
};

// Result modifiers (CSV "Result Modifiers" tab) — supporting messages only.
export const MODIFIER_MESSAGES = {
    BMI_30_OR_LESS:
        'Your BMI is within our screening limits for T-Shape 2 and awake liposuction. An individual assessment is still required.',
    BMI_30_TO_40: 'Your BMI is within our screening limit for awake liposuction and above our limit for T-Shape 2.',
    BMI_OVER_40: 'Consultation is available to discuss your goals and next steps.',
    SKIN_EXCISION:
        'Your BMI is within our screening limit for awake skin excision. A consultation is needed to assess suitability.',
    downtime: {
        none: 'You prefer an option that fits around your daily routine. We can discuss how that affects your choices.',
        short: 'You can make room for 1–3 days away from your usual routine. Recovery needs will be discussed at your consultation.',
        flexible: 'You have flexibility to plan recovery around the option discussed.',
    },
    change: {
        subtle: 'Your goal is a subtle improvement.',
        noticeable: 'Your goal is a noticeable change in shape.',
        major: 'Your goal is a major transformation. A consultation can help define what is realistic.',
    },
    budget: {
        low: 'Your selected budget is $350–$1,800. We can explain which options and treatment plans may fit.',
        mid: 'Your selected budget is $2,000–$5,000.',
        high: 'Your selected budget is $6,000–$9,000.',
        top: 'Your selected budget is $10,000–$15,000.',
        undecided: 'You would like to compare treatment options and prices before setting a budget.',
    },
};

export const SKIN_EXCISION_360_NOTE =
    'Awake skin excision addresses the abdomen rather than the full 360 midsection.';

export const INVALID_MESSAGE =
    'Please complete the required questions and check your height and weight so we can provide your guidance.';
